import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import {
  calculateActualSessionTimePassedSeconds,
  getNowTimestamp,
  getWorkoutSessionsState,
} from 'src/utils/workSessions.utils';
import { StoredWorkSessionsState, WorkSessionsState } from 'src/models/workSessions';
import initialState from './workSessionsInitialState';

export const slice = createSlice({
  name: 'workSessions',
  initialState,
  reducers: {
    updateState: (state: WorkSessionsState, action: PayloadAction<StoredWorkSessionsState>) =>
      getWorkoutSessionsState(action.payload),
    toggleIsPaused: (state: WorkSessionsState) => {
      // if paused
      if (state.playTimestamp === null) {
        const actualSessionTimePassed = calculateActualSessionTimePassedSeconds(state);
        const isEndOfSession = actualSessionTimePassed === state.session.durationSeconds;
        const isLastSession = state.currentSession === state.sessionsCount;

        if (isEndOfSession && isLastSession) {
          return;
        }
        if (isEndOfSession) {
          state.sessionTimePassedSeconds = 0;
          state.currentSession += 1;
        }
        state.playTimestamp = getNowTimestamp();
      } else {
        state.sessionTimePassedSeconds = calculateActualSessionTimePassedSeconds(state);
        state.playTimestamp = null;
      }
    },
    updateTick: (state: WorkSessionsState) => {
      const newTickTS = getNowTimestamp();
      const { session } = state;

      if (state.playTimestamp) {
        const actualSessionTimePassed = calculateActualSessionTimePassedSeconds(state);
        const newTimePassed = Math.min(
          actualSessionTimePassed + (newTickTS - state.tickTimestamp) / 1000,
          session.durationSeconds
        );
        const isWorkFinished =
          newTimePassed >= session.workDurationSeconds && state.sessionTimePassedSeconds < session.workDurationSeconds;
        const isBreakFinished = newTimePassed === session.durationSeconds;

        if (isWorkFinished || isBreakFinished) {
          state.sessionTimePassedSeconds = newTimePassed;
          state.playTimestamp = null;
          state.notification = isWorkFinished ? 'Work session is done! Great job 👍' : 'Break is finished!';
        }
      } else {
        const timeLeft =
          (state.sessionsCount - state.currentSession + 1) * session.durationSeconds - state.sessionTimePassedSeconds;

        if (timeLeft) {
          state.endTimestamp = newTickTS + timeLeft * 1000;
        }
      }

      state.tickTimestamp = newTickTS;
    },
    rewindWorkBreak: (state: WorkSessionsState) => {
      const actualSessionTimePassed = calculateActualSessionTimePassedSeconds(state);

      if (!actualSessionTimePassed && state.currentSession > 1) {
        state.currentSession -= 1;
        state.sessionTimePassedSeconds = state.session.workDurationSeconds;
      } else if (actualSessionTimePassed > state.session.workDurationSeconds) {
        state.sessionTimePassedSeconds = state.session.workDurationSeconds;
      } else {
        state.sessionTimePassedSeconds = 0;
      }
      state.playTimestamp = null;
    },
    skipWorkBreak: (state: WorkSessionsState) => {
      const actualSessionTimePassed = calculateActualSessionTimePassedSeconds(state);
      const isLastSession = state.currentSession === state.sessionsCount;

      if (actualSessionTimePassed < state.session.workDurationSeconds) {
        state.sessionTimePassedSeconds = state.session.workDurationSeconds;
      } else if (isLastSession) {
        state.sessionTimePassedSeconds = state.session.durationSeconds;
      } else {
        state.sessionTimePassedSeconds = 0;
        state.currentSession += 1;
      }
      state.playTimestamp = null;
    },
    setSessionTimePassed: (state: WorkSessionsState, action: PayloadAction<number>) => {
      state.sessionTimePassedSeconds = action.payload;
      state.playTimestamp = null;
    },
    startManualRewind: (state: WorkSessionsState) => {
      state.isManualRewind = true;
    },
    stopManualRewind: (state: WorkSessionsState) => {
      state.isManualRewind = false;
    },
    resetSessions: (state: WorkSessionsState) => {
      state.currentSession = 1;
      state.sessionTimePassedSeconds = 0;
      state.playTimestamp = null;
    },
    clearNotification: (state: WorkSessionsState) => {
      state.notification = initialState.notification;
    },
  },
});

export const {
  toggleIsPaused,
  updateTick,
  rewindWorkBreak,
  skipWorkBreak,
  clearNotification,
  resetSessions,
  updateState,
  setSessionTimePassed,
  startManualRewind,
  stopManualRewind,
} = slice.actions;

export default slice.reducer;
